import { useState, useCallback, useRef, useEffect } from 'react';
import { useApolloClient, useMutation, useQuery } from 'react-apollo';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'query-string';

import { accountUpdate as accountUpdateMutation } from '../../../../graphql/mutations/account';
import { checkout as checkoutMutation } from '../../../../graphql/mutations/checkout';
import { loginSuccess } from '../../../../actions/auth-actions';
import { openAlert } from '../../../../actions/modal-actions';
import { getErrorString } from '../../../../util/normalize-errors';
import {
  accountEmailExists,
  accountPhoneNumberExists,
  MeQuery,
} from '../../../../graphql/queries/account';
import { sentryCaptureMessage } from '../../../../util/sentry';
import { EventItems } from '../../../../graphql/queries/events';
import { gtm } from '../../../../util/analytics';
import { formatPriceForDisplay } from '../../../../util/format-money';
import { ALBUM_SUBTYPE } from '../../../../constants/albums';
import usePhoneAuth from '../../../../components/auth/hooks/usePhoneAuth';
import useGoogleRecaptcha from '../../../../services/recaptcha/useGoogleRecaptcha';

export const WEDDINGS_REGISTRATION_STEP = {
  SKU_SELECTION: 'SKU_SELECTION',
  DETAILS: 'DETAILS',
  PIN_VERIFY: 'PIN_VERIFY',
  CHECKOUT: 'CHECKOUT',
};

const useWeddingsRegistration = () => {
  const location = useLocation();
  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const [step, setStep] = useState(WEDDINGS_REGISTRATION_STEP.DETAILS);
  const userId = useRef(null);
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const onErrorModalOpen = useCallback(state => dispatch(openAlert(state)), [
    dispatch,
  ]);
  const onLogin = useCallback(() => dispatch(loginSuccess()));
  const client = useApolloClient();
  const {
    data: { eventItems = [] },
  } = useQuery(EventItems);
  const [accountUpdate] = useMutation(accountUpdateMutation);
  const [checkout] = useMutation(checkoutMutation);
  const { onTokenRequest } = useGoogleRecaptcha();
  const {
    loading: phoneAuthLoading,
    pinSent,
    requireHumanInput,
    blockLogin,
    onPhoneAuthSubmit,
    onPhoneAuthVerify,
    onResendPhoneAuthCode,
  } = usePhoneAuth();

  useEffect(() => {
    if (pinSent && step !== WEDDINGS_REGISTRATION_STEP.PIN_VERIFY) {
      setStep(WEDDINGS_REGISTRATION_STEP.PIN_VERIFY);
    }
  }, [pinSent]);

  const createEventUrl = `/app/shutterbug/new?eventType=${ALBUM_SUBTYPE.WEDDINGS}`;

  const { promo_code: defaultPromoCode } =
    qs.parse(location.search ?? '') ?? {};

  const onPhoneVerificationRequest = async values => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);
      const token = await onTokenRequest({ action: 'login' });
      await onPhoneAuthSubmit(
        values.phone_number,
        true,
        token,
        values.force_allow_action,
      );
    } catch (responseError) {
      onErrorModalOpen({
        message: 'There was an error with your registration',
        info: responseError?.request_id ?? getErrorString(responseError),
      });
    } finally {
      setLoading(false);
    }
  };

  const onPinCodeResend = async values => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);
      const token = await onTokenRequest({ action: 'login' });
      await onResendPhoneAuthCode(
        values.phone_number,
        true,
        token,
        values.force_allow_action,
      );
    } catch (responseError) {
      onErrorModalOpen({
        message: 'There was an error sending the PIN.',
        info: responseError?.request_id ?? getErrorString(responseError),
      });
    } finally {
      setLoading(false);
    }
  };

  const onGetAuthenticatedUser = async () => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);
      const {
        data: { me },
      } = await client.query({
        query: MeQuery,
        fetchPolicy: 'network-only',
      });

      userId.current = me.id;
      setStep(WEDDINGS_REGISTRATION_STEP.SKU_SELECTION);
    } catch (responseError) {
      onErrorModalOpen({
        message: 'There was an error with your registration',
        info: responseError?.request_id ?? getErrorString(responseError),
      });
    } finally {
      setLoading(false);
    }
  };

  const onPinVerify = async values => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);
      await onPhoneAuthVerify(values.phone_number, values.code);

      const {
        data: { me },
      } = await client.query({
        query: MeQuery,
        fetchPolicy: 'network-only',
      });

      userId.current = me.id;

      await accountUpdate({
        variables: {
          input: {
            id: me.id,
            name: `${values.first_name} ${values.last_name}`.trim(),
            client_store_items: [
              {
                key: 'shutterbug_signup_timestamp',
                value: moment().valueOf().toString(),
              },
            ],
            email_address: values.email_address,
            state_id: values.state_id ?? undefined,
          },
        },
      });

      onLogin();

      // From 4/11/2022, people will now land within the standard
      // shutterbug create flow and go from there. No specific Wedding registration with checkout and whatnot.
      // The checkout/sku selection code was left in case this decision gets scratched.
      history.push(createEventUrl);
    } catch (responseError) {
      onErrorModalOpen({
        message: 'There was an error with your registration',
        info: responseError?.request_id ?? getErrorString(responseError),
      });
    } finally {
      setLoading(false);
    }
  };

  const onEventItemSelected = () => {
    if (authenticated) {
      setStep(WEDDINGS_REGISTRATION_STEP.CHECKOUT);
    } else {
      setStep(WEDDINGS_REGISTRATION_STEP.DETAILS);
    }
  };

  const onCheckout = async values => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);

      const response = await checkout({
        variables: {
          input: {
            account_id: userId.current,
            // Might be null/undefined if is free checkout
            stripe_token: values.stripe_token?.id,
            email_address: values.stripe_token?.email ?? values.email,
            stripe_payment_method_id:
              values.stripe_token?.stripe_payment_method_id,
            stripe_connect_account_id:
              values.stripe_token?.stripe_connect_account_id,

            promo_code: values.promo_code,
            event_items: [
              {
                event_item_id: values.event_item_id,
                quantity: 1,
              },
            ],
            platform: 'WEB',
          },
        },
      });

      // Track conversion
      gtm.trackEcommerce({
        data: {
          id: response.data.checkout.transaction.id,
          productId: 'weddings',
          revenue: formatPriceForDisplay(
            response.data.checkout.total_price.value,
          ),
          tax: 0,
          shipping: 0,
        },
      });

      // Forces log in and redirect to /app
      onLogin();
      history.replace('/app');
    } catch (responseError) {
      onErrorModalOpen({
        message: 'There was an error with your registration',
        info: responseError?.request_id ?? getErrorString(responseError),
      });
      sentryCaptureMessage('WEDDINGS SIGNUP - Registration Error - Checkout', {
        level: 'error',
        extra: {
          error: responseError,
        },
      });
      setLoading(false);
    }
  };

  const canGoBack = (() => {
    switch (step) {
      case WEDDINGS_REGISTRATION_STEP.PIN_VERIFY:
      case WEDDINGS_REGISTRATION_STEP.CHECKOUT:
        return true;
      default:
        return false;
    }
  })();

  const onGoBack = () => {
    switch (step) {
      case WEDDINGS_REGISTRATION_STEP.PIN_VERIFY:
        setStep(WEDDINGS_REGISTRATION_STEP.DETAILS);

        break;
      case WEDDINGS_REGISTRATION_STEP.CHECKOUT:
        setStep(WEDDINGS_REGISTRATION_STEP.SKU_SELECTION);

        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (authenticated && !userId?.current) {
      onGetAuthenticatedUser();
    }
  }, [authenticated]);

  const onAccountEmailVerify = async emailAddress => {
    setLoading(true);

    try {
      const {
        data: { accountEmailExists: accountEmailExistsResult } = {},
      } = await client.query({
        query: accountEmailExists,
        variables: {
          email_address: emailAddress,
        },
      });

      return accountEmailExistsResult;
    } catch {
      return false;
    } finally {
      setLoading(false);
    }
  };

  const onAccountPhoneNumberVerify = async phoneNumber => {
    setLoading(true);

    try {
      const {
        data: { accountPhoneNumberExists: accountPhoneNumberExistsResult } = {},
      } = await client.query({
        query: accountPhoneNumberExists,
        variables: {
          phone_number: phoneNumber,
        },
      });

      return accountPhoneNumberExistsResult;
    } catch {
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    step,
    setStep,
    onPhoneVerificationRequest,
    onPinVerify,
    onPinCodeResend,
    onEventItemSelected,
    onCheckout,
    loading: loading || phoneAuthLoading,
    eventItems,
    onGoBack,
    canGoBack,
    defaultPromoCode,
    onAccountEmailVerify,
    onAccountPhoneNumberVerify,
    createEventUrl,
    requireHumanInput,
    blockLogin,
  };
};

export default useWeddingsRegistration;
